import { type FormEvent, useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;

interface TemplateRow {
  template: string;
  locale: 'lt' | 'en';
  hasOverride: boolean;
  defaultSubject: string;
  defaultHtml: string;
  defaultText: string;
  override?: {
    id: string;
    subject: string;
    htmlBody: string;
    textBody: string;
  };
}

export function EmailTemplatesPage() {
  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    api<TemplateRow[]>('/email-templates')
      .then(setRows)
      .catch((err: unknown) => setError(String(err)));
  }, [reload]);

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!rows) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">{t('email_templates.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('email_templates.intro')}</p>

      <div className="space-y-3">
        {rows.map((r) => {
          const key = `${r.template}:${r.locale}`;
          return (
            <div key={key} className="rounded border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono text-sm">{r.template}</span>
                  <span className="ml-2 rounded-full border border-border bg-muted px-2 py-0.5 text-xs uppercase">
                    {r.locale}
                  </span>
                  {r.hasOverride ? (
                    <span className="ml-2 rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100">
                      {t('email_templates.overridden')}
                    </span>
                  ) : (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {t('email_templates.default')}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(editing === key ? null : key)}
                  className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted"
                >
                  {editing === key ? t('common.cancel') : t('email_templates.edit')}
                </button>
              </div>
              {editing === key ? (
                <Editor
                  template={r.template}
                  locale={r.locale}
                  initialSubject={r.override?.subject ?? r.defaultSubject}
                  initialHtml={r.override?.htmlBody ?? r.defaultHtml}
                  initialText={r.override?.textBody ?? r.defaultText}
                  overrideId={r.override?.id ?? null}
                  onSaved={() => {
                    setEditing(null);
                    setReload((k) => k + 1);
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Editor({
  template,
  locale,
  initialSubject,
  initialHtml,
  initialText,
  overrideId,
  onSaved,
}: {
  template: string;
  locale: 'lt' | 'en';
  initialSubject: string;
  initialHtml: string;
  initialText: string;
  overrideId: string | null;
  onSaved: () => void;
}) {
  const [subject, setSubject] = useState(initialSubject);
  const [html, setHtml] = useState(initialHtml);
  const [text, setText] = useState(initialText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/email-templates', {
        method: 'PUT',
        body: { template, locale, subject, htmlBody: html, textBody: text },
      });
      onSaved();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  async function revert() {
    if (!overrideId) return;
    if (!window.confirm(t('email_templates.revert_confirm'))) return;
    try {
      await api(`/email-templates/${overrideId}`, { method: 'DELETE' });
      onSaved();
    } catch {
      setError(t('common.error'));
    }
  }

  return (
    <form onSubmit={(e) => void save(e)} className="space-y-2 text-sm">
      <label className="block space-y-1">
        <span className="font-medium">{t('email_templates.subject')}</span>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          className="w-full rounded border border-border bg-background px-2 py-1.5"
        />
      </label>
      <label className="block space-y-1">
        <span className="font-medium">{t('email_templates.html')}</span>
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          rows={10}
          required
          className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono text-xs"
        />
      </label>
      <label className="block space-y-1">
        <span className="font-medium">{t('email_templates.text')}</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          required
          className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono text-xs"
        />
      </label>
      {error ? <div className="text-red-600">{error}</div> : null}
      <div className="flex justify-between gap-2 pt-2">
        {overrideId ? (
          <button
            type="button"
            onClick={() => void revert()}
            className="rounded border border-amber-500 px-3 py-1.5 text-xs text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/30"
          >
            {t('email_templates.revert')}
          </button>
        ) : (
          <span />
        )}
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-xs disabled:opacity-50"
        >
          {busy ? t('common.loading') : t('common.save')}
        </button>
      </div>
    </form>
  );
}
